// Patience curve utilities — pure, testable, no side effects.
// Time is days since release. Y is a simple accumulated "external pressure" score.

import type { Signal } from './types'

export interface CurvePoint {
  t: number          // days since release (or 0 for pre-release private)
  external: number   // cumulative external signal weight
  internal: number   // private conviction markers
  label?: string
}

export function daysBetween(a: string, b: string): number {
  const ms = new Date(b).getTime() - new Date(a).getTime()
  return Math.max(0, ms / (1000 * 60 * 60 * 24))
}

export function buildPatienceCurve(
  releasedAt: string | undefined,
  signals: Signal[],
  releaseNote?: string
): CurvePoint[] {
  if (!releasedAt) {
    // Pre-release: just show the private conviction as a single point
    return [{ t: 0, external: 0, internal: 55, label: 'Decision made' }]
  }

  const points: CurvePoint[] = []
  points.push({ t: 0, external: 0, internal: 62, label: 'Released' })

  let ext = 0
  let int = 62

  const sorted = [...signals].sort((a, b) => a.loggedAt.localeCompare(b.loggedAt))

  for (const s of sorted) {
    const t = daysBetween(releasedAt, s.loggedAt)
    const weight = (s.magnitude || 3) * (s.valence === 0 ? 0.6 : s.valence > 0 ? 1.0 : 0.7)
    if (s.kind === 'internal') {
      int = Math.max(20, Math.min(95, int + (s.valence >= 0 ? +4 : -3)))
      points.push({ t: Math.max(t, 0.01), external: ext, internal: int, label: s.note.slice(0, 48) })
    } else {
      ext = Math.max(0, Math.min(100, ext + weight * 6))
      points.push({ t: Math.max(t, 0.01), external: ext, internal: int, label: s.note.slice(0, 48) })
    }
  }

  // Always end with a current tail point so the curve feels alive
  const lastT = points.length ? points[points.length - 1].t : 1
  points.push({ t: Math.max(lastT + 0.5, 2), external: ext, internal: int })

  return points
}

export function computeVelocity(points: CurvePoint[]): number {
  if (points.length < 2) return 0
  const first = points[0]
  const last = points[points.length - 1]
  const dt = Math.max(0.5, last.t - first.t)
  return Math.round(((last.external - first.external) / dt) * 10) / 10
}
